import React, { useState } from 'react';
import { Field, useField } from 'formik';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { Select } from '@tymate/elise/components';
import { FormField } from '../../ui/forms';
import { ErrorMessage } from '../Forms';

const WarningContainer = styled.div`
  margin-top: 4px;
  padding: 8px 12px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  color: #856404;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WarningIcon = styled.svg`
  flex-shrink: 0;
`;

const LoadingContainer = styled.div`
  padding: 8px 16px;
  text-align: center;
  color: #666;
  font-style: italic;
`;

const AddressSelectField = ({ onChange, setValueLabel, addressWarning, ...props }) => {
  return (
    <Field name={props.name}>
      {({ field, form }) => {
        const { touched, errors } = form;
        const hasError = errors?.[field.name] && touched?.[field.name];

        if (form.values[props.name] === null) {
          setValueLabel('');
        }

        return (
          <FormField>
            <Select
              {...props}
              hasError={hasError}
              onChange={option => {
                onChange(option);
              }}
              variant="input"
              authorizeCustomInput={true}
            />
            {!props.shouldHideErrorMessage && <ErrorMessage {...field} />}
            {addressWarning && (
              <WarningContainer>
                <WarningIcon
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </WarningIcon>
                {addressWarning}
              </WarningContainer>
            )}
          </FormField>
        );
      }}
    </Field>
  );
};

export const AddressAutocompleteField = ({
  placeholder = 'Entrez une adresse...',
  label,
  showOptionalLabel = false,
  zipCodeField,
  cityField,
  setFieldValue,
  ...props
}) => {
  const [field] = useField(props.name);
  const [search, setSearch] = useState('');
  const [value, setValueLabel] = useState(
    field.value || props.initialValueLabel || '',
  );
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addressWarning, setAddressWarning] = useState('');

  // Update value label when field value changes
  React.useEffect(() => {
    if (field.value !== value) {
      setValueLabel(field.value || '');
    }
  }, [field.value, value]);



  // Validate address in BAN database
  const validateAddress = React.useCallback(
    debounce(async (address, zipCode, city) => {
      if (!address || address.length < 3) {
        setAddressWarning('');
        return;
      }

      try {
        const params = new URLSearchParams({
          q: address + ', ' + zipCode + ' ' + city,
          limit: 1,
          index: 'address', // Use address index for geocoding
          autocomplete: '0', // Exact search
        });

        const response = await fetch(
          `https://data.geopf.fr/geocodage/search?${params}`,
        );

        if (response.ok) {
          const data = await response.json();
          const features = data.features || [];

          if (features.length === 0) {
            setAddressWarning('Cette adresse n\'a pas été trouvée dans la base de données officielle.');
          } else {
            // Check if the found address is close enough to the input
            const foundAddress = features[0].properties;
            const foundAddressString = `${foundAddress.housenumber || ''} ${foundAddress.street || ''}`.trim();
            
            // Simple similarity check - if addresses are very different, show warning
            const inputLower = address.toLowerCase();
            const foundLower = foundAddressString.toLowerCase();
            
            if (!foundLower.includes(inputLower) && !inputLower.includes(foundLower)) {
              setAddressWarning('Cette adresse n\'a pas été trouvée dans la base de données officielle.');
            } else {
              setAddressWarning('');
            }
          }
        } else {
          setAddressWarning('Impossible de vérifier cette adresse pour le moment.');
        }
      } catch (error) {
        console.error('Address validation error:', error);
        setAddressWarning('Erreur lors de la vérification de l\'adresse.');
      }
    }, 500),
    [],
  );

  // Validate initial address value on mount
  React.useEffect(() => {
    const initialValue = field.value || props.initialValueLabel || '';
    if (initialValue && initialValue.length >= 3) {
      validateAddress(initialValue);
    }
  }, []); // Empty dependency array means this runs only on mount

  // Search addresses with debounce
  const searchAddresses = React.useCallback(
    debounce(async query => {
      if (!query || query.length < 2) {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          q: query,
          limit: 50,
          index: 'address', // Use address index for geocoding
          autocomplete: '1',
        });



        const response = await fetch(
          `https://data.geopf.fr/geocodage/search?${params}`,
        );

        if (response.ok) {
          const data = await response.json();
          const features = data.features || [];

          const addressOptions = features.map(feature => {
            const props = feature.properties;
            const formattedAddress = `${props.housenumber || ''} ${
              props.street || ''
            }`.trim();

            let label = '';
            if (props.housenumber && props.street) {
              label += `${props.housenumber} ${props.street}`;
            } else if (props.street) {
              label += props.street;
            } else if (props.name) {
              label += props.name;
            }

            if (props.postcode && props.city) {
              label += `, ${props.postcode} ${props.city}`;
            } else if (props.city) {
              label += `, ${props.city}`;
            }

            return {
              value: formattedAddress,
              label: label,
              properties: props, // Store original properties for zip/city filling
            };
          });

          setOptions(addressOptions);
        } else if (response.status === 400) {
          // Bad request (e.g., invalid query parameters) - expected when no results found
          setOptions([]);
        } else {
          // Other error statuses - log only unexpected errors
          console.error('Address API error:', response.status, response.statusText);
          setOptions([]);
        }
      } catch (error) {
        // Only log network errors, not expected bad requests
        if (error.name !== 'TypeError' || !error.message.includes('fetch')) {
          console.error('Network error:', error);
        }
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [],
  );

  // Update search and fetch options
  React.useEffect(() => {
    searchAddresses(search);
  }, [search, searchAddresses]);

  const handleAddressSelect = option => {
    const { value, properties } = option;

    // Set the field value and display label
    setFieldValue(field.name, value);
    setValueLabel(value);

    // Validate the selected address
    validateAddress(value, properties.postcode, properties.city);

    // Fill zip code field if specified and postcode exists
    if (zipCodeField && properties?.postcode) {
      setFieldValue(zipCodeField, properties.postcode);
    }

    // Fill city field if specified and city exists
    if (cityField && properties?.city) {
      setFieldValue(cityField, properties.city);
    }
  };

  // Handle manual input changes
  const handleInputChange = (newValue) => {
    setValueLabel(newValue);
    setFieldValue(field.name, newValue);
    
    // Clear warning when user manually types
    setAddressWarning('');
  };

  return (
    <AddressSelectField
      {...props}
      label={label}
      showOptionalLabel={showOptionalLabel}
      options={options}
      isSearchable
      onUpdateSearch={setSearch}
      initialValueLabel={value}
      setValueLabel={setValueLabel}
      onChange={handleAddressSelect}
      onInputChange={handleInputChange}
      isField
      placeholder={placeholder}
      shouldHideErrorMessage={props.shouldHideErrorMessage}
      addressWarning={addressWarning}
    >
      {isLoading && (
        <LoadingContainer>
          Recherche en cours...
        </LoadingContainer>
      )}
    </AddressSelectField>
  );
};
